/**
 * PublicListFeed — premium card-style feed layout for public list pages.
 */

import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../../theme/ThemeContext';
import envConfig from '../../config/envConfig';
import { getCurrencyDisplay } from '../../utils/currencies';
import { localeDateIn, parseInstant, toDisplayString, zoneOf } from '../../utils/utils';
import JsonCell from '../common/JsonCell';
import PublicTagBadges from './PublicTagBadges';

const isJsonField = (f) => !!f && (f.data_type === 'json' || f.data_type === 'jsonb');

function resolveFileUrl(value) {
  if (!value) return null;
  if (value.startsWith('http://') || value.startsWith('https://')) return value;
  return `${envConfig.fileServerUrl}/public/${value}`;
}

const BADGE_COLOR_MAP = {
  primary: '#2d6cf6', success: '#16a34a', warning: '#d97706',
  danger: '#dc2626', info: '#0891b2', secondary: '#64748b',
};

const TITLE_LIMIT = 60;
const SUBTITLE_LIMIT = 80;
const DESC_LIMIT = 120;

function trunc(val, limit) {
  const s = val == null ? '' : String(val);
  return s.length > limit ? s.slice(0, limit) + '…' : s;
}

export default function PublicListFeed({ records = [], fields = [], mapping = {}, layoutConfig = {}, detailEnabled = true, detailButtonLabel = '', tableSlug: tableSlugProp }) {
  const navigate = useNavigate();
  const { tableSlug: tableSlugParam } = useParams();
  const tableSlug = tableSlugProp || tableSlugParam;
  const { theme } = useTheme();

  const badgeColors = mapping.badge_colors || {};
  const accentColor = theme.primary;

  const fieldMap = Object.fromEntries(fields.map(f => [f.name, f]));
  const imageMappedMeta = mapping.image_field ? fieldMap[mapping.image_field] : null;
  const isRealImageField = imageMappedMeta?.data_type === 'image';

  const css = `
    .pl-row {
      display: flex;
      align-items: center;
      gap: 1.1rem;
      padding: 1rem 1.3rem;
      background: #ffffff;
      border-radius: 16px;
      border: 1.5px solid ${accentColor}2a;
      border-left: 4px solid ${accentColor};
      box-shadow: 0 2px 10px rgba(0,0,0,0.07), 0 4px 20px ${accentColor}18;
      transition: box-shadow 0.26s cubic-bezier(0.4,0,0.2,1),
                  transform 0.26s cubic-bezier(0.4,0,0.2,1),
                  border-color 0.26s ease,
                  background 0.22s ease;
    }
    .pl-row:hover {
      box-shadow: 0 12px 44px ${accentColor}30, 0 4px 16px rgba(0,0,0,0.09);
      transform: translateX(5px);
      border-color: ${accentColor}50;
      background: #ffffff;
    }
    .pl-row:active {
      transform: scale(0.99);
    }

    .pl-thumb {
      flex-shrink: 0;
      width: 88px;
      height: 88px;
      border-radius: 14px;
      overflow: hidden;
      background: linear-gradient(145deg, ${accentColor}1e 0%, ${accentColor}48 100%);
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 2px 12px ${accentColor}28;
      transition: transform 0.26s cubic-bezier(0.4,0,0.2,1), box-shadow 0.26s ease;
    }
    .pl-row:hover .pl-thumb {
      transform: scale(1.05);
      box-shadow: 0 4px 18px ${accentColor}3a;
    }

    .pl-img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
      opacity: 0;
      transition: opacity 0.4s ease;
    }
    .pl-img.loaded {
      opacity: 1;
    }

    .pl-arrow-btn {
      width: 30px;
      height: 30px;
      border-radius: 50%;
      background: ${accentColor}10;
      border: 1.5px solid ${accentColor}30;
      display: flex;
      align-items: center;
      justify-content: center;
      color: ${accentColor};
      font-size: 0.78rem;
      cursor: pointer;
      transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease, color 0.2s ease;
    }
    .pl-arrow-btn:hover {
      background: ${accentColor};
      border-color: ${accentColor};
      color: #fff;
      transform: scale(1.15);
    }

    .arcee-pub__title    { font-weight: 700; font-size: 1rem; line-height: 1.35; color: #0f172a; }
    .arcee-pub__subtitle { font-size: 0.82rem; color: #334155; font-weight: 500; }
    .arcee-pub__desc     { font-size: 0.79rem; color: #64748b; line-height: 1.55; }
    .arcee-pub__badge {
      background: var(--arcee-pub-badge-bg, transparent);
      color: var(--arcee-pub-badge-color, inherit);
      border: 1.5px solid var(--arcee-pub-badge-border, transparent);
      font-size: 0.67rem; font-weight: 700;
      padding: 3px 10px; border-radius: 999px;
      letter-spacing: 0.05em; text-transform: uppercase;
      white-space: nowrap;
    }
    .arcee-pub__date {
      display: inline-flex; align-items: center; gap: 4px;
      font-size: 0.68rem; color: #94a3b8; font-weight: 500;
    }
    .arcee-pub__cost {
      display: inline-flex; align-items: center; gap: 4px;
      font-size: 0.68rem; color: #16a34a; font-weight: 700;
      background: #16a34a0d; padding: 2px 8px;
      border-radius: 999px; border: 1px solid #16a34a28;
    }
    .arcee-pub__cta {
      display: inline-flex; align-items: center; gap: 4px;
      font-size: 0.73rem; font-weight: 700;
      color: #fff; background: ${accentColor};
      padding: 5px 12px; border-radius: 999px;
      white-space: nowrap;
      box-shadow: 0 2px 10px ${accentColor}44;
      transition: box-shadow 0.2s ease, transform 0.2s ease;
    }
  `;

  if (records.length === 0) {
    return (
      <>
        <style>{css}</style>
        <div style={{
          textAlign: 'center', padding: '4rem 2rem',
          background: '#fff', borderRadius: 18,
          boxShadow: `0 2px 10px rgba(0,0,0,0.08), 0 8px 30px ${accentColor}1c`,
          border: `1.5px solid ${accentColor}2c`,
        }}>
          <div style={{
            width: 72, height: 72, borderRadius: '50%',
            background: `linear-gradient(135deg, ${accentColor}18 0%, ${accentColor}30 100%)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 32, margin: '0 auto 16px',
            boxShadow: `0 4px 20px ${accentColor}22`,
          }}>
            <i className="bi bi-list-ul" style={{ fontSize: 28, color: accentColor, opacity: 0.7 }} />
          </div>
          <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#0f172a', marginBottom: 6 }}>No records found</div>
          <p style={{ color: '#94a3b8', fontSize: '0.84rem', margin: 0, fontWeight: 400 }}>Try adjusting your filters</p>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{css}</style>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {records.map((record) => {
          const title = record[mapping.title_field] ?? null;
          const subtitle = mapping.subtitle_field ? record[mapping.subtitle_field] : null;
          const imageVal = (isRealImageField && mapping.image_field) ? record[mapping.image_field] : null;
          const desc = mapping.description_field ? record[mapping.description_field] : null;
          const tags = mapping.tags_field ? record[mapping.tags_field] : null;

          const titleStr = (title != null && title !== '') ? toDisplayString(title) : '';
          const subtitleStr = (subtitle != null && subtitle !== '') ? toDisplayString(subtitle) : null;
          const descStr = (desc != null && desc !== '') ? toDisplayString(desc) : null;
          const tagsStr = (tags != null && tags !== '') ? String(tags) : null;
          const subtitleIsJson = isJsonField(fieldMap[mapping.subtitle_field]);
          const descIsJson = isJsonField(fieldMap[mapping.description_field]);
          const badge = mapping.badge_field ? record[mapping.badge_field] : null;
          const dateVal = mapping.date_field ? record[mapping.date_field] : null;
          const costVal = mapping.cost_field ? record[mapping.cost_field] : null;

          const badgeKey = badge ? (badgeColors[badge] || 'primary') : 'primary';
          const badgeBg = BADGE_COLOR_MAP[badgeKey] || BADGE_COLOR_MAP.primary;
          return (
            <div
              key={record.id}
              className="pl-row arcee-pub--list"
              data-record-id={record.id}
              style={{ cursor: detailEnabled ? 'pointer' : 'default' }}
              onClick={detailEnabled ? () => navigate(`/p/${tableSlug}/${record.id}`) : undefined}
            >
              {/* ── Thumbnail ── */}
              <div className="pl-thumb arcee-pub__thumb">
                {imageVal ? (
                  <img
                    src={resolveFileUrl(imageVal)}
                    alt={titleStr || ''}
                    className="pl-img"
                    loading="lazy"
                    onLoad={e => e.target.classList.add('loaded')}
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <>
                    <div style={{
                      position: 'absolute', inset: 0,
                      backgroundImage: `radial-gradient(circle, ${accentColor}28 1.5px, transparent 1.5px)`,
                      backgroundSize: '10px 10px',
                    }} />
                    <img
                      src="/favicon.svg"
                      alt="Arcee"
                      style={{ position: 'relative', width: 36, height: 36, opacity: 0.45, userSelect: 'none' }}
                    />
                  </>
                )}
              </div>

              {/* ── Content ── */}
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>

                {mapping.title_field && titleStr && (
                  <div
                    className="arcee-pub__title"
                    title={titleStr.length > TITLE_LIMIT ? titleStr : undefined}
                  >
                    {trunc(titleStr, TITLE_LIMIT)}
                  </div>
                )}

                {subtitleStr && (
                  <div className="arcee-pub__subtitle"
                    title={!subtitleIsJson && subtitleStr.length > SUBTITLE_LIMIT ? subtitleStr : undefined}>
                    {subtitleIsJson ? <JsonCell value={subtitle} /> : trunc(subtitleStr, SUBTITLE_LIMIT)}
                  </div>
                )}

                {descStr && (
                  <div className="arcee-pub__desc"
                    title={!descIsJson && descStr.length > DESC_LIMIT ? descStr : undefined}>
                    {descIsJson ? <JsonCell value={desc} /> : trunc(descStr, DESC_LIMIT)}
                  </div>
                )}

                {tagsStr && (
                  <div style={{ marginTop: 2 }}>
                    <PublicTagBadges value={tagsStr} bgColor={mapping.tag_bg_color} accentColor={accentColor} />
                  </div>
                )}

                {(dateVal || (costVal != null && costVal !== '')) && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
                    {dateVal && (
                      <span className="arcee-pub__date">
                        <i className="bi bi-calendar3" style={{ fontSize: '0.6rem' }} />
                        {localeDateIn(parseInstant(dateVal), { year: 'numeric', month: 'short', day: 'numeric' }, zoneOf(record))}
                      </span>
                    )}
                    {costVal != null && costVal !== '' && (
                      <span className="arcee-pub__cost">
                        <span style={{ fontWeight: 700 }}>{getCurrencyDisplay(mapping.cost_currency)}</span>
                        {costVal}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* ── Right: badge + action ── */}
              <div style={{
                flexShrink: 0, display: 'flex', flexDirection: 'column',
                alignItems: 'flex-end', justifyContent: 'center', gap: 8,
              }}>
                {badge && (
                  <span
                    className="arcee-pub__badge"
                    style={{
                      '--arcee-pub-badge-bg': `${badgeBg}14`,
                      '--arcee-pub-badge-color': badgeBg,
                      '--arcee-pub-badge-border': `${badgeBg}35`,
                    }}
                  >
                    {badge}
                  </span>
                )}

                {detailEnabled && (
                  detailButtonLabel ? (
                    <span
                      className="arcee-pub__cta"
                      onMouseEnter={e => {
                        e.currentTarget.style.boxShadow = `0 4px 16px ${accentColor}66`;
                        e.currentTarget.style.transform = 'scale(1.04)';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.boxShadow = `0 2px 10px ${accentColor}44`;
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    >
                      {detailButtonLabel}
                      <i className="bi bi-arrow-right" style={{ fontSize: '0.64rem' }} />
                    </span>
                  ) : (
                    <div className="pl-arrow-btn">
                      <i className="bi bi-arrow-right" />
                    </div>
                  )
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
